import getYear from 'date-fns/getYear';
import React, { useCallback } from 'react';
import { ReactDatePickerProps } from 'react-datepicker';
import { Box } from '@tanium/react-box';
import { ChevronLeftIcon, ChevronRightIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { ThemeFontWeights } from '@tanium/theme-data';
import { useTranslation } from './localization';
import { MonthHeaderText, YearHeaderText } from './themeDefinitions';

type Props = Parameters<NonNullable<ReactDatePickerProps['renderCustomHeader']>>[0];

const CustomHeaderRendererBase = ({
  changeYear,
  date,
  decreaseMonth,
  increaseMonth,
  nextMonthButtonDisabled,
  prevMonthButtonDisabled,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const handlePreviousYearClick = useCallback(() => {
    changeYear(getYear(date) - 1);
  }, [changeYear, date]);

  const handleNextYearClick = useCallback(() => {
    changeYear(getYear(date) + 1);
  }, [changeYear, date]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <IconButton
          icon={ChevronLeftIcon}
          label={t('Previous month')}
          height={20}
          width={20}
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        />
        <Box
          color={MonthHeaderText}
          fontSize={18}
          fontWeight={ThemeFontWeights.Bold}
          lineHeight="20px"
          data-date-picker-month
        >
          {t('{{ date, MONTH_ONLY }}', { date })}
        </Box>
        <IconButton
          icon={ChevronRightIcon}
          label={t('Next month')}
          height={20}
          width={20}
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        />
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center" my={1}>
        <IconButton
          icon={ChevronLeftIcon}
          label={t('Previous year')}
          height={10}
          width={10}
          onClick={handlePreviousYearClick}
          disabled={prevMonthButtonDisabled}
        />
        <Box mx={2} color={YearHeaderText} data-date-picker-year>
          {t('{{ date, YEAR_ONLY }}', { date })}
        </Box>
        <IconButton
          icon={ChevronRightIcon}
          label={t('Next year')}
          height={10}
          width={10}
          onClick={handleNextYearClick}
          disabled={nextMonthButtonDisabled}
        />
      </Box>
    </>
  );
};

/**
 * Function for rendering the custom header for the DatePicker. Responsibilities include year and
 * month navigation.
 */
export const customHeaderRenderer = (props: Props) => <CustomHeaderRendererBase {...props} />;
